import { useState, useEffect } from "react";
import axios from "axios";
import { DollarSign, TrendingUp, Target, Handshake, AlertTriangle, StickyNote, PhoneCall, CalendarClock, Mail, Check, Wallet } from "lucide-react";
import { API } from "@/utils/constants";

export const isStale = (d) => !["won", "lost"].includes(d.stage) && Date.now() - new Date(d.updated_at || d.created_at).getTime() > 7 * 24 * 3600 * 1000;

const fmt = (v) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(v || 0);
const STAGE_META = [
  { key: "lead", label: "Lead", color: "bg-slate-400" },
  { key: "contacted", label: "Contacted", color: "bg-sky-500" },
  { key: "proposal", label: "Proposal", color: "bg-amber-500" },
  { key: "won", label: "Won", color: "bg-emerald-500" },
  { key: "lost", label: "Lost", color: "bg-red-400" },
];
const ACT_ICONS = { note: StickyNote, call: PhoneCall, meeting: CalendarClock, email: Mail, task: Check };

const StatCard = ({ icon: Icon, label, value, sub, color, testId }) => (
  <div className="bg-card border rounded-2xl p-5" data-testid={testId}>
    <div className="flex items-center gap-2 mb-2">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${color.bg}`}><Icon className={`h-4 w-4 ${color.text}`} /></div>
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
    </div>
    <div className="text-2xl font-bold">{value}</div>
    {sub && <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>}
  </div>
);

export const CRMDashboard = ({ deals, contacts, stats, onOpenDeal }) => {
  const [activities, setActivities] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [salesOrders, setSalesOrders] = useState([]);
  useEffect(() => {
    axios.get(`${API}/crm/activities`).then((r) => setActivities(r.data.slice(0, 8))).catch(() => {});
    axios.get(`${API}/crm/invoices`).then((r) => setInvoices(r.data)).catch(() => {});
    axios.get(`${API}/crm/expenses`).then((r) => setExpenses(r.data)).catch(() => {});
    axios.get(`${API}/crm/documents`, { params: { doc_type: "quote" } }).then((r) => setQuotes(r.data)).catch(() => {});
    axios.get(`${API}/crm/documents`, { params: { doc_type: "sales_order" } }).then((r) => setSalesOrders(r.data)).catch(() => {});
  }, []);

  const won = deals.filter((d) => d.stage === "won").length;
  const lost = deals.filter((d) => d.stage === "lost").length;
  const winRate = won + lost > 0 ? Math.round((won / (won + lost)) * 100) : null;
  const openDeals = deals.filter((d) => !["won", "lost"].includes(d.stage));
  const staleDeals = deals.filter(isStale);
  const maxCount = Math.max(1, ...STAGE_META.map((s) => deals.filter((d) => d.stage === s.key).length));
  const contactName = (id) => contacts.find((c) => c.id === id)?.name;

  const invSum = (statuses) => invoices.filter((i) => statuses.includes(i.status)).reduce((s, i) => s + (i.total || 0), 0);
  const invoiced = invSum(["sent", "paid", "overdue"]);
  const paid = invSum(["paid"]);
  const outstanding = invSum(["sent"]);
  const overdue = invSum(["overdue"]);
  const draftCount = invoices.filter((i) => i.status === "draft").length;
  const paidPct = invoiced > 0 ? Math.round((paid / invoiced) * 100) : 0;

  const months = [...Array(6)].map((_, i) => {
    const d = new Date();
    d.setMonth(d.getMonth() - (5 - i));
    return { key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`, label: d.toLocaleString("en-US", { month: "short" }) };
  });
  const monthly = months.map((m) => ({
    ...m,
    total: invoices.filter((i) => i.status === "paid" && (i.paid_at || i.updated_at || i.created_at || "").startsWith(m.key)).reduce((s, i) => s + (i.total || 0), 0),
  }));
  const maxMonthly = Math.max(1, ...monthly.map((m) => m.total));

  const expensesTotal = expenses.reduce((s, x) => s + (x.amount || 0), 0);
  const netProfit = paid - expensesTotal;
  const qAccepted = quotes.filter((q) => q.status === "accepted").length;
  const qDeclined = quotes.filter((q) => q.status === "declined").length;
  const quoteRate = qAccepted + qDeclined > 0 ? Math.round((qAccepted / (qAccepted + qDeclined)) * 100) : null;
  const openQuotes = quotes.filter((q) => ["draft", "sent"].includes(q.status));
  const openSO = salesOrders.filter((s) => ["draft", "confirmed"].includes(s.status));

  return (
    <div className="space-y-6" data-testid="crm-dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={DollarSign} label="Pipeline Value" value={fmt(stats?.pipeline_value)} sub={`${openDeals.length} open deal${openDeals.length === 1 ? "" : "s"}`} color={{ bg: "bg-sky-500/10", text: "text-sky-500" }} testId="dash-stat-pipeline" />
        <StatCard icon={TrendingUp} label="Won Revenue" value={fmt(stats?.won_value)} sub={`${won} deal${won === 1 ? "" : "s"} won`} color={{ bg: "bg-emerald-500/10", text: "text-emerald-500" }} testId="dash-stat-won" />
        <StatCard icon={Target} label="Win Rate" value={winRate === null ? "—" : `${winRate}%`} sub={winRate === null ? "No closed deals yet" : `${won} won / ${lost} lost`} color={{ bg: "bg-violet-500/10", text: "text-violet-500" }} testId="dash-stat-winrate" />
        <StatCard icon={Handshake} label="Total Deals" value={deals.length} sub={`${contacts.length} contact${contacts.length === 1 ? "" : "s"}`} color={{ bg: "bg-amber-500/10", text: "text-amber-500" }} testId="dash-stat-total" />
      </div>

      <div className="bg-card border rounded-2xl p-5" data-testid="dash-revenue">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold flex items-center gap-2"><Wallet className="h-4 w-4 text-emerald-500" />Cash flow — invoiced vs paid</h3>
          {draftCount > 0 && <span className="text-[11px] text-muted-foreground">{draftCount} draft{draftCount === 1 ? "" : "s"} not counted</span>}
        </div>
        {invoices.length === 0 ? (
          <p className="text-xs text-muted-foreground py-4 text-center">No invoices yet — create one in the Invoices tab (or from a won deal) to track cash flow here.</p>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
              <div data-testid="dash-rev-invoiced">
                <p className="text-[11px] text-muted-foreground mb-0.5">Invoiced</p>
                <p className="text-xl font-bold">{fmt(invoiced)}</p>
              </div>
              <div data-testid="dash-rev-paid">
                <p className="text-[11px] text-muted-foreground mb-0.5">Paid</p>
                <p className="text-xl font-bold text-emerald-600">{fmt(paid)}</p>
              </div>
              <div data-testid="dash-rev-outstanding">
                <p className="text-[11px] text-muted-foreground mb-0.5">Awaiting payment</p>
                <p className="text-xl font-bold text-sky-600">{fmt(outstanding)}</p>
              </div>
              <div data-testid="dash-rev-overdue">
                <p className="text-[11px] text-muted-foreground mb-0.5">Overdue</p>
                <p className={`text-xl font-bold ${overdue > 0 ? "text-red-500" : "text-muted-foreground"}`}>{fmt(overdue)}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex-1 h-3 rounded-full bg-muted overflow-hidden flex" data-testid="dash-rev-bar">
                {invoiced > 0 && (
                  <>
                    <div className="h-full bg-emerald-500 transition-all" style={{ width: `${(paid / invoiced) * 100}%` }} />
                    <div className="h-full bg-sky-400 transition-all" style={{ width: `${(outstanding / invoiced) * 100}%` }} />
                    <div className="h-full bg-red-400 transition-all" style={{ width: `${(overdue / invoiced) * 100}%` }} />
                  </>
                )}
              </div>
              <span className="text-xs font-semibold whitespace-nowrap" data-testid="dash-rev-paid-pct">{paidPct}% collected</span>
            </div>
            <div className="flex gap-4 mt-2 text-[10px] text-muted-foreground">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" />Paid</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-sky-400" />Awaiting</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400" />Overdue</span>
            </div>
            {paid > 0 && (
              <div className="mt-5 pt-4 border-t" data-testid="dash-monthly-revenue">
                <p className="text-xs font-semibold text-muted-foreground mb-3">Paid revenue — last 6 months</p>
                <div className="flex items-end gap-2 h-24">
                  {monthly.map((m) => (
                    <div key={m.key} className="flex-1 flex flex-col items-center gap-1" data-testid={`dash-month-${m.key}`}>
                      <span className="text-[9px] text-muted-foreground">{m.total > 0 ? fmt(m.total) : ""}</span>
                      <div className="w-full rounded-t-md bg-emerald-500/80 transition-all" style={{ height: `${Math.max(m.total > 0 ? 8 : 2, (m.total / maxMonthly) * 64)}px` }} />
                      <span className="text-[10px] text-muted-foreground">{m.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {(paid > 0 || expenses.length > 0) && (
        <div className="bg-card border rounded-2xl p-5" data-testid="dash-profit">
          <h3 className="text-sm font-semibold mb-4 flex items-center gap-2"><TrendingUp className="h-4 w-4 text-emerald-500" />Profit overview</h3>
          <div className="grid grid-cols-3 gap-4 mb-1">
            <div data-testid="dash-profit-revenue">
              <p className="text-[11px] text-muted-foreground mb-0.5">Paid revenue</p>
              <p className="text-xl font-bold text-emerald-600">{fmt(paid)}</p>
            </div>
            <div data-testid="dash-profit-expenses">
              <p className="text-[11px] text-muted-foreground mb-0.5">Expenses</p>
              <p className="text-xl font-bold text-red-500">−{fmt(expensesTotal)}</p>
              <p className="text-[10px] text-muted-foreground">{expenses.length} expense{expenses.length === 1 ? "" : "s"} logged</p>
            </div>
            <div data-testid="dash-profit-net">
              <p className="text-[11px] text-muted-foreground mb-0.5">Net profit</p>
              <p className={`text-xl font-bold ${netProfit >= 0 ? "text-emerald-600" : "text-red-500"}`}>{netProfit < 0 ? "−" : ""}{fmt(Math.abs(netProfit))}</p>
              {paid > 0 && <p className="text-[10px] text-muted-foreground">{Math.round((netProfit / paid) * 100)}% margin</p>}
            </div>
          </div>
          {(() => {
            const monthlyPnl = months.map((m) => ({
              ...m,
              rev: monthly.find((x) => x.key === m.key)?.total || 0,
              exp: expenses.filter((x) => (x.date || "").startsWith(m.key)).reduce((s, x) => s + (x.amount || 0), 0),
            }));
            const maxPnl = Math.max(1, ...monthlyPnl.map((m) => Math.max(m.rev, m.exp)));
            if (!monthlyPnl.some((m) => m.rev > 0 || m.exp > 0)) return null;
            return (
              <div className="mt-4 pt-4 border-t" data-testid="dash-monthly-pnl">
                <p className="text-xs font-semibold text-muted-foreground mb-3">Revenue vs expenses — last 6 months</p>
                <div className="flex items-end gap-2 h-20">
                  {monthlyPnl.map((m) => (
                    <div key={m.key} className="flex-1 flex flex-col items-center gap-1">
                      <div className="w-full flex items-end justify-center gap-0.5 h-14">
                        <div className="w-2/5 rounded-t bg-emerald-500/80" style={{ height: `${Math.max(m.rev > 0 ? 4 : 1, (m.rev / maxPnl) * 56)}px` }} title={`Revenue ${fmt(m.rev)}`} />
                        <div className="w-2/5 rounded-t bg-red-400/80" style={{ height: `${Math.max(m.exp > 0 ? 4 : 1, (m.exp / maxPnl) * 56)}px` }} title={`Expenses ${fmt(m.exp)}`} />
                      </div>
                      <span className="text-[10px] text-muted-foreground">{m.label}</span>
                    </div>
                  ))}
                </div>
                <div className="flex gap-4 mt-2 text-[10px] text-muted-foreground">
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" />Revenue</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400" />Expenses</span>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {(quotes.length > 0 || salesOrders.length > 0) && (
        <div className="bg-card border rounded-2xl p-5" data-testid="dash-documents">
          <h3 className="text-sm font-semibold mb-4">Sales documents</h3>
          <div className="grid grid-cols-3 gap-4">
            <div data-testid="dash-quote-rate">
              <p className="text-[11px] text-muted-foreground mb-0.5">Quote acceptance</p>
              <p className="text-xl font-bold">{quoteRate === null ? "—" : `${quoteRate}%`}</p>
              <p className="text-[10px] text-muted-foreground">{qAccepted} accepted / {qDeclined} declined</p>
            </div>
            <div data-testid="dash-open-quotes">
              <p className="text-[11px] text-muted-foreground mb-0.5">Open quotes</p>
              <p className="text-xl font-bold">{openQuotes.length}</p>
              <p className="text-[10px] text-muted-foreground">{fmt(openQuotes.reduce((s, q) => s + (q.total || 0), 0))} pending</p>
            </div>
            <div data-testid="dash-open-so">
              <p className="text-[11px] text-muted-foreground mb-0.5">Open sales orders</p>
              <p className="text-xl font-bold">{openSO.length}</p>
              <p className="text-[10px] text-muted-foreground">{fmt(openSO.reduce((s, q) => s + (q.total || 0), 0))} to fulfil</p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-card border rounded-2xl p-5" data-testid="dash-deals-by-stage">
          <h3 className="text-sm font-semibold mb-4">Deals by stage</h3>
          <div className="space-y-3">
            {STAGE_META.map((s) => {
              const stageDeals = deals.filter((d) => d.stage === s.key);
              const value = stageDeals.reduce((sum, d) => sum + (d.value || 0), 0);
              return (
                <div key={s.key}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="font-medium">{s.label}</span>
                    <span className="text-muted-foreground">{stageDeals.length} · {fmt(value)}</span>
                  </div>
                  <div className="h-2 rounded-full bg-muted overflow-hidden">
                    <div className={`h-full rounded-full ${s.color} transition-all`} style={{ width: `${(stageDeals.length / maxCount) * 100}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-card border rounded-2xl p-5" data-testid="dash-stale-deals">
          <h3 className="text-sm font-semibold mb-4 flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-500" />Needs attention <span className="text-xs font-normal text-muted-foreground">— no activity for 7+ days</span></h3>
          {staleDeals.length === 0 ? (
            <p className="text-xs text-muted-foreground py-6 text-center">All open deals are up to date. Nice work!</p>
          ) : (
            <div className="space-y-2 max-h-56 overflow-y-auto">
              {staleDeals.map((d) => (
                <button key={d.id} type="button" onClick={() => onOpenDeal(d)} className="w-full flex items-center justify-between gap-3 bg-amber-500/5 border border-amber-500/20 rounded-lg p-2.5 text-left hover:bg-amber-500/10 transition-colors" data-testid={`stale-deal-${d.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{d.title}</p>
                    <p className="text-[11px] text-muted-foreground">{contactName(d.contact_id) || "No contact"} · idle since {new Date(d.updated_at || d.created_at).toLocaleDateString()}</p>
                  </div>
                  <span className="text-xs font-semibold text-emerald-600 shrink-0">{fmt(d.value)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="bg-card border rounded-2xl p-5" data-testid="dash-recent-activity">
        <h3 className="text-sm font-semibold mb-4">Recent activity</h3>
        {activities.length === 0 ? (
          <p className="text-xs text-muted-foreground py-4 text-center">No activity logged yet — open a contact and log a call, note or meeting.</p>
        ) : (
          <div className="space-y-2">
            {activities.map((a) => {
              const Icon = ACT_ICONS[a.type] || StickyNote;
              return (
                <div key={a.id} className="flex items-start gap-2.5 bg-muted/40 rounded-lg p-2.5" data-testid={`dash-activity-${a.id}`}>
                  <Icon className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs">{a.text}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">{a.type}{contactName(a.contact_id) ? ` · ${contactName(a.contact_id)}` : ""} · {new Date(a.created_at).toLocaleString()}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
